import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, AlertDescription, AlertIcon, AlertTitle, Badge, Box, Button, Flex } from '@chakra-ui/react';

import { DocAccessType, EvidenceOfIncomeDocs, FamilySponsorship, ServiceProviderType } from 'types';
import {
  BirthCertificateDocs,
  CustomRequestMap,
  DepositDoc,
  EducationDocsMap,
  FamilySponsorshipDocMap,
  ICourse,
  IFinance,
  IFinanceDocuments,
  IHistory,
  IPersonalInfo,
  IRegionConfig,
  IRisk,
  OrganisationDocsMap,
  OtherSponsorship,
  ScholarshipDocsMap,
} from '../../../../types';

import Deposit from '../../../molecules/financeDocumentSection/deposit';
import { FileUploadModal } from '../../modals/fileUpload/index';
import EvidenceOfIncomeSection from '../../../molecules/financeDocumentSection/evidenceOfIncome';
import FamilySection from '../../../molecules/financeDocumentSection/family';
import EducationSponsor from '../../../molecules/financeDocumentSection/education';
import ScholarshipSponsor from '../../../molecules/financeDocumentSection/scholarship';
import NoDocuments from '../../../atoms/no-doc-placeholder';
import { formatCurrency } from '../../../../utilities/formatting';
import FileRejectModal from '../../modals/fileReject/container';
import FileRescindRejectionModal from '../../modals/fileRescindRejection';
import FileDeleteModal from '../../modals/fileDelete/container';
import CustomRequestDocumentModal from '../../modals/customRequestDoc/container';
import CustomRequestDocument from '../../../molecules/financeDocumentSection/customRequest';
import GovernmentOrCompany from '../../../molecules/financeDocumentSection/governmentOrCompany';
import { UserRole } from '../../../../store/auth/types';
import {
  hasPerm,
  isAgentOrAgencyRole,
  isEnrolyStaff,
  isPartnerRole,
  isUniStaffRole,
  P,
} from '../../../../utilities/roles/permissions';
import { IModal } from '../../../../store/ui/types';
import { bankStatementName, findBirthCertSponsor, getLeadSponsorByKey } from '../../../../utilities/documents';
import { hasFundingRisks } from '../../../../utilities/risks';
import { getInvalidFundingSources } from '../../../../utilities/getInvalidFundingSources';
import { useIsSectionLocked } from '../../../../hooks/useIsSectionLocked';
import { ApplicationState } from '../../../../store';
import { SectionId } from '../../../molecules/studentLeftNav/types';
import LockedSectionWarning from '../../../molecules/locked-section-warning';
import CASBypassWarning from '../../../molecules/casBypassWarning/casBypassWarning';
import { useCustomNavigate } from '../../../../hooks/useCustomNavigate';
import { getStudentFinDocs } from '../../../../store/student/actions';
import { DocumentRequestButtons } from '../../../DocumentRequestButtons';
import { SectionGroup } from '../../../Finger/SectionGroup';
import { DocumentViewer } from '../../../organisms/DocumentViewer';

export interface IDispatchProps {
  onUpdateRisks: (source: string, clearedRisks: any) => void;
  openFinanceDocumentModal: (
    sponsorType: string,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    modalTitle?: string,
  ) => void;
  openUploadFinanceModal: (
    sponsorType: string,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    modalTitle: string,
    fileNames: string[],
    metadata?: any,
  ) => void;
  onApproveDoc: (
    sponsorType: string,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    docStatus?: string,
    docPrevStatus?: string,
  ) => void;
  onAcknowledgeDoc: (
    sponsorType: string,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    docStatus?: string,
    docPrevStatus?: string,
  ) => void;
}

export interface IProps {
  hasRequiredDocs: boolean;
  hasInfoOnlyDocs: boolean;
  hasStaffOnlyDocs: boolean;
  hasAgentOnlyDocs: boolean;
  documents: IFinanceDocuments;
  userRole: UserRole;
  serviceProviderType?: ServiceProviderType;
  personalInfo: IPersonalInfo;
  history: IHistory;
  openModal: IModal | null;
  financials: IFinance;
  risks: IRisk;
  courses?: Array<ICourse>;
  config: IRegionConfig;
  totalAmountToProve: number;
}
type Props = IProps & IDispatchProps;

export interface IInvalidFundingSource {
  label: string;
  contributionAmount: number;
  fundingType: string;
  isBankAccepted: boolean;
}

function DocumentDashboard(props: Props) {
  const [imageArray, setImageArray] = useState<string[] | undefined>();
  const [previewOpen, toggleOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const offer = useSelector(({ student }: ApplicationState) => student.offer);
  const isChecklistSectionLocked = useIsSectionLocked(SectionId.CHECKLIST, offer.status);

  const { offerId = '' } = useParams();

  const { navigateTo } = useCustomNavigate();
  const onResume = (url: string) => navigateTo(`${offerId}/${url}`);

  const currentUserRole = useSelector(({ auth }: ApplicationState) => auth.role);

  const isAgent = isAgentOrAgencyRole(currentUserRole);
  const isStaff = isUniStaffRole(currentUserRole);
  const isEnrolyOrPartner = isEnrolyStaff(currentUserRole) || isPartnerRole(currentUserRole);

  const canResume = hasPerm(props.userRole, P.CHECKLIST_EDIT) && !isChecklistSectionLocked;
  const canOpen = hasPerm(props.userRole, P.ALL_DOCUMENTS_PREVIEW, props.serviceProviderType);
  const canApprove = hasPerm(props.userRole, P.ALL_DOCUMENTS_APPROVE);
  const canReject = hasPerm(props.userRole, P.ALL_DOCUMENTS_REJECT);
  const canRequest = hasPerm(props.userRole, P.CUSTOM_DOCUMENTS_REQUEST);
  const canDelete = hasPerm(props.userRole, P.FINANCE_DOCUMENTS_DELETE);
  const canAcknowledge = hasPerm(props.userRole, P.ALL_DOCUMENTS_ACKNOWLEDGE);
  const showRisks = hasPerm(props.userRole, P.FINANCE_DOCUMENTS_FLAGS);
  const canCheckOffRisks = hasPerm(props.userRole, P.CHECK_OFF_RISKS);
  const canUploadStaffOnly = hasPerm(props.userRole, P.UPLOAD_STAFF_ONLY_DOCS) && (isStaff || isEnrolyOrPartner);
  const canUploadAgentOnly =
    hasPerm(props.userRole, P.UPLOAD_AGENT_ONLY_DOCS) && (isAgent || isStaff || isEnrolyOrPartner); // Staff and enroly has access of agent docs as well

  const isCasBypass = useSelector(({ student }: ApplicationState) => student.offer.casBypass);
  const isSectionLocked = useIsSectionLocked(SectionId.GENERAL_DOC, offer.status);

  const agentUploadLock = isAgent && isSectionLocked;
  const canUpload = hasPerm(props.userRole, P.GENERAL_DOCUMENTS_UPLOAD) && !agentUploadLock;

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getStudentFinDocs());
  }, []);
  const handleUpdateDocRisksClick = (target: string, docKey: string, source: string) => {
    let clearedRisks = {};
    const financeDocsRisk = props.risks?.financeDocsRisk ?? {};

    switch (target) {
      case 'educationSponsorship':
      case 'familySponsorship':
      case 'scholarshipSponsorship':
      case 'organisationSponsorship':
      case 'evidenceOfIncome':
      case 'birthCertificate':
        const foundDoc = financeDocsRisk[target].find(docRisk => docRisk.id === docKey);
        clearedRisks = {
          financeDocsRisk: {
            [target]: [
              ...(props.risks?.clearedRisks && props.risks?.clearedRisks?.financeDocsRisk
                ? props.risks?.clearedRisks?.financeDocsRisk[target]
                : []),
              ...(foundDoc ? [foundDoc] : []),
            ],
          },
        };
        break;
      default:
        break;
    }

    props.onUpdateRisks(`Finance Document - ${source}`, clearedRisks);
  };

  const handleUpdateFinanceRisksClick = (target: string, docKey: string, source: string) => {
    let clearedRisks = {};
    const { fundingRisk } = props.risks;

    switch (target) {
      case 'organisationSponsorship':
      case 'educationSponsorship':
      case 'familySponsorship':
      case 'other':
        const foundDoc = fundingRisk[target].find(docRisk => docRisk.id === docKey);
        clearedRisks = {
          fundingRisk: {
            [target]: [
              ...(props.risks?.clearedRisks && props.risks?.clearedRisks?.fundingRisk
                ? props.risks?.clearedRisks?.fundingRisk[target]
                : []),
              ...(foundDoc ? [foundDoc] : []),
            ],
          },
        };
        break;
      default:
        break;
    }

    props.onUpdateRisks(`Finance Info - ${source}`, clearedRisks);
  };

  const checkCheckedOffDocRisks = (target: string, docKey: string): boolean => {
    if (props.risks?.clearedRisks?.financeDocsRisk) {
      const financeDocsRisk = props.risks?.clearedRisks?.financeDocsRisk ?? {};

      switch (target) {
        case 'educationSponsorship':
        case 'familySponsorship':
        case 'scholarshipSponsorship':
        case 'organisationSponsorship':
        case 'evidenceOfIncome':
        case 'birthCertificate':
          return hasFundingRisks(financeDocsRisk[target], docKey);
        default:
          return false;
      }
    }
    return false;
  };

  const checkCheckedOffFinanceRisks = (target: string, docKey: string): boolean => {
    if (props.risks?.clearedRisks?.fundingRisk) {
      const fundingRisk = props.risks?.clearedRisks?.fundingRisk;

      switch (target) {
        case 'organisationSponsorship':
        case 'educationSponsorship':
        case 'familySponsorship':
        case 'other':
          return hasFundingRisks(fundingRisk[target], docKey);
        default:
          return false;
      }
    }
    return false;
  };

  const getInvalidFunding = () => {
    const [invalidFamilySponsorship, invalidOtherSponsorship] = getInvalidFundingSources(props.financials);
    const invalidFundingSources: IInvalidFundingSource[] = [];

    Object.values(invalidFamilySponsorship ?? {}).forEach((sponsor: FamilySponsorship) => {
      invalidFundingSources.push({
        label: sponsor.accountType,
        contributionAmount: sponsor.totalAmount || 0,
        fundingType: bankStatementName(sponsor.sponsorType),
        isBankAccepted: true,
      });
    });

    Object.values(invalidOtherSponsorship ?? {}).forEach((sponsor: OtherSponsorship) => {
      invalidFundingSources.push({
        label: sponsor.otherName,
        contributionAmount: sponsor.sponsorshipAmount || 0,
        fundingType: 'Other',
        isBankAccepted: true,
      });
    });

    let invalidFundingSection = null;
    if (invalidFundingSources.length) {
      invalidFundingSection = (
        <Alert status="warning" data-selector="invalid-funding" mb={6}>
          <AlertIcon />

          <Box flexGrow={1}>
            <AlertTitle mb={2}>Invalid Funding Sources</AlertTitle>

            <AlertDescription>
              The following funding sources may be <b>invalid</b> and cannot be factored into the funding calculator.
            </AlertDescription>

            {invalidFundingSources.map((source: IInvalidFundingSource, index: number) => {
              return (
                <Flex mt={3} ml={6} columnGap={12} key={`invalid-fund-${index}`}>
                  <Box flexGrow={1} fontWeight={'medium'} py={1} lineHeight={6}>
                    {source.label}
                  </Box>

                  <Box py={1} lineHeight={6} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    £{formatCurrency(source.contributionAmount)}
                  </Box>

                  <Button onClick={() => onResume('finances/step-1')} size={'sm'}>
                    Edit
                  </Button>
                </Flex>
              );
            })}
          </Box>
        </Alert>
      );
    }
    return invalidFundingSection;
  };

  const sponsorWithBirthCert = findBirthCertSponsor(
    props.financials.familySponsorship ?? {},
    props.financials.educationSponsorship ?? {},
  );

  const renderDocs = (infoOnly = false, userAccess?: DocAccessType) => {
    return Reflect.ownKeys(props.documents)
      .sort()
      .map((key: any) => {
        const isDocKey = key in props?.documents && typeof props.documents?.[key] === 'object';
        if (!isDocKey) {
          return null;
        }
        switch (key) {
          case 'educationSponsorship': {
            const educationSponsorMap = props.documents[key] as EducationDocsMap;

            return Object.entries(educationSponsorMap).map(([eduKey, educationSponsor]) => {
              const eduSponsorFinance = props.financials.educationSponsorship?.[eduKey];
              if (!eduSponsorFinance) {
                return null;
              }

              let birthCertificate: BirthCertificateDocs | undefined = undefined;
              if (sponsorWithBirthCert === eduKey) {
                birthCertificate = props.documents.birthCertificate;
              }

              return (
                <EducationSponsor
                  key={eduKey}
                  id={eduKey}
                  infoOnly={infoOnly}
                  userAccess={userAccess}
                  financials={eduSponsorFinance}
                  risk={props.risks.fundingRisk.educationSponsorship}
                  docRisk={props.risks.financeDocsRisk}
                  documents={educationSponsor}
                  birthCertificate={birthCertificate}
                  canResume={canResume}
                  canUpload={canUpload}
                  canOpen={canOpen}
                  canApprove={canApprove}
                  canReject={canReject}
                  isDeleting={isDeleting}
                  canAcknowledge={canAcknowledge}
                  showRisks={showRisks}
                  canCheckOff={canCheckOffRisks}
                  isCheckedOffDocRisks={(docType: string, docKey: string) => {
                    return checkCheckedOffDocRisks(docType, docKey);
                  }}
                  onCheckOffDocRisks={(docType: string, docKey: string, docTitle: string) => {
                    handleUpdateDocRisksClick(docType, docKey, docTitle);
                  }}
                  isCheckedOffFinanceRisks={(docType: string, docKey: string) => {
                    return checkCheckedOffFinanceRisks(docType, docKey);
                  }}
                  onCheckOffFinanceRisks={(docType: string, docKey: string, docTitle: string) => {
                    handleUpdateFinanceRisksClick(docType, docKey, docTitle);
                  }}
                  onResume={onResume}
                  onUploadClick={(sponsorType: string, docType: string | null, docKey: string, metaData?: string) => {
                    props.openUploadFinanceModal(
                      sponsorType,
                      sponsorType === 'birthCertificate' ? null : eduKey,
                      docType,
                      docKey,
                      sponsorType === 'birthCertificate' ? 'Birth Certificate' : eduSponsorFinance.loanName,
                      sponsorType === 'birthCertificate'
                        ? birthCertificate![docType!][docKey].fileNames
                        : educationSponsor[docKey].fileNames,
                      metaData,
                    );
                  }}
                  onOpenClick={(sponsorType: string, docType: string | null, docKey: string) => {
                    setImageArray(
                      sponsorType === 'birthCertificate'
                        ? birthCertificate![docType!][docKey].signedUrls
                        : educationSponsor[docKey].signedUrls,
                    );
                    toggleOpen(true);
                  }}
                  onApproveClick={(sponsorType: string, docType: string | null, docKey: string) => {
                    props.onApproveDoc(
                      sponsorType,
                      sponsorType === 'birthCertificate' ? null : eduKey,
                      docType,
                      docKey,
                      sponsorType === 'birthCertificate'
                        ? birthCertificate![docType!][docKey].status
                        : educationSponsor[docKey].status,
                      sponsorType === 'birthCertificate'
                        ? birthCertificate![docType!][docKey].prevStatus
                        : educationSponsor[docKey].prevStatus,
                    );
                  }}
                  onRejectClick={(sponsorType: string, docType: string | null, docKey: string, modalTitle: string) =>
                    props.openFinanceDocumentModal(
                      sponsorType,
                      sponsorType === 'birthCertificate' ? null : eduKey,
                      `reject-${docType}`,
                      docKey,
                      modalTitle,
                    )
                  }
                  onRescindRejectClick={(
                    sponsorType: string,
                    docType: string | null,
                    docKey: string,
                    modalTitle: string,
                  ) =>
                    props.openFinanceDocumentModal(
                      sponsorType,
                      sponsorType === 'birthCertificate' ? null : eduKey,
                      `rescind-reject-${docType}`,
                      docKey,
                      modalTitle,
                    )
                  }
                  onDeleteClick={(sponsorType: string, docType: string | null, docKey: string, modalTitle: string) =>
                    props.openFinanceDocumentModal(
                      sponsorType,
                      sponsorType === 'birthCertificate' ? null : eduKey,
                      `delete-${docType}`,
                      docKey,
                      modalTitle,
                    )
                  }
                  onAcknowledgeClick={(sponsorType: string, docType: string | null, docKey: string) =>
                    props.onAcknowledgeDoc(
                      sponsorType,
                      sponsorType === 'birthCertificate' ? null : eduKey,
                      docType,
                      docKey,
                      sponsorType === 'birthCertificate'
                        ? birthCertificate![docType!][docKey].status
                        : educationSponsor[docKey].status,
                      sponsorType === 'birthCertificate'
                        ? birthCertificate![docType!][docKey].prevStatus
                        : educationSponsor[docKey].prevStatus,
                    )
                  }
                  userRole={props.userRole}
                />
              );
            });
          }
          case 'familySponsorship': {
            const familySponsorMap = props.documents[key] as FamilySponsorshipDocMap;

            return Object.entries(familySponsorMap).map(([famKey, familySponsor]) => {
              const familySponsorFinance = props.financials.familySponsorship?.[famKey];
              let birthCertificate: BirthCertificateDocs | undefined = undefined;
              if (sponsorWithBirthCert === famKey) {
                birthCertificate = props.documents.birthCertificate;
              }

              if (!familySponsorFinance && !birthCertificate) {
                return null;
              }
              if (familySponsorFinance.accountType === 'Other') {
                return null; // these documents are managed in 'Invalid Funding' section
              }

              return (
                <FamilySection
                  key={famKey}
                  id={famKey}
                  infoOnly={infoOnly}
                  userAccess={userAccess}
                  documents={familySponsor}
                  financials={familySponsorFinance}
                  risk={props.risks.fundingRisk.familySponsorship}
                  docRisk={props.risks.financeDocsRisk}
                  birthCertificate={birthCertificate}
                  canResume={canResume}
                  canUpload={canUpload}
                  canOpen={canOpen}
                  canApprove={canApprove}
                  canReject={canReject}
                  isDeleting={isDeleting}
                  canAcknowledge={canAcknowledge}
                  showRisks={showRisks}
                  canCheckOff={canCheckOffRisks}
                  isCheckedOffDocRisks={(docType: string, docKey: string) => {
                    return checkCheckedOffDocRisks(docType, docKey);
                  }}
                  onCheckOffDocRisks={(docType: string, docKey: string, docTitle: string) => {
                    handleUpdateDocRisksClick(docType, docKey, docTitle);
                  }}
                  isCheckedOffFinanceRisks={(docType: string, docKey: string) => {
                    return checkCheckedOffFinanceRisks(docType, docKey);
                  }}
                  onCheckOffFinanceRisks={(docType: string, docKey: string, docTitle: string) => {
                    handleUpdateFinanceRisksClick(docType, docKey, docTitle);
                  }}
                  onResume={onResume}
                  onUploadClick={(sponsorType: string, docType: string | null, docKey: string, modalTitle: string) => {
                    props.openUploadFinanceModal(
                      sponsorType,
                      sponsorType === 'birthCertificate' ? null : famKey,
                      docType,
                      docKey,
                      modalTitle,
                      sponsorType === 'birthCertificate'
                        ? props.documents.birthCertificate[docType!][docKey].fileNames
                        : props.documents.familySponsorship[famKey][docType!][docKey].fileNames,
                    );
                  }}
                  onOpenClick={(sponsorType: string, docType: string | null, docKey: string) => {
                    setImageArray(
                      sponsorType === 'birthCertificate'
                        ? props.documents.birthCertificate[docType!][docKey].signedUrls
                        : props.documents.familySponsorship[famKey][docType!][docKey].signedUrls!,
                    );
                    toggleOpen(true);
                  }}
                  onApproveClick={(sponsorType: string, docType: string | null, docKey: string) =>
                    props.onApproveDoc(
                      sponsorType,
                      sponsorType === 'birthCertificate' ? null : famKey,
                      docType,
                      docKey,
                      sponsorType === 'birthCertificate'
                        ? props.documents.birthCertificate[docType!][docKey].status
                        : props.documents.familySponsorship[famKey][docType!][docKey].status,
                      sponsorType === 'birthCertificate'
                        ? props.documents.birthCertificate[docType!][docKey].prevStatus
                        : props.documents.familySponsorship[famKey][docType!][docKey].prevStatus,
                    )
                  }
                  onRejectClick={(sponsorType: string, docType: string | null, docKey: string, modalTitle: string) =>
                    props.openFinanceDocumentModal(
                      sponsorType,
                      sponsorType === 'birthCertificate' ? null : famKey,
                      `reject-${docType}`,
                      docKey,
                      modalTitle,
                    )
                  }
                  onRescindRejectClick={(
                    sponsorType: string,
                    docType: string | null,
                    docKey: string,
                    modalTitle: string,
                  ) =>
                    props.openFinanceDocumentModal(
                      sponsorType,
                      sponsorType === 'birthCertificate' ? null : famKey,
                      `rescind-reject-${docType}`,
                      docKey,
                      modalTitle,
                    )
                  }
                  onDeleteClick={(sponsorType: string, docType: string | null, docKey: string, modalTitle: string) =>
                    props.openFinanceDocumentModal(
                      sponsorType,
                      sponsorType === 'birthCertificate' ? null : famKey,
                      `delete-${docType}`,
                      docKey,
                      modalTitle,
                    )
                  }
                  onAcknowledgeClick={(sponsorType: string, docType: string | null, docKey: string) =>
                    props.onAcknowledgeDoc(
                      sponsorType,
                      sponsorType === 'birthCertificate' ? null : famKey,
                      docType,
                      docKey,
                      sponsorType === 'birthCertificate'
                        ? props.documents.birthCertificate[docType!][docKey].status
                        : props.documents.familySponsorship[famKey][docType!][docKey].status,
                      sponsorType === 'birthCertificate'
                        ? props.documents.birthCertificate[docType!][docKey].prevStatus
                        : props.documents.familySponsorship[famKey][docType!][docKey].prevStatus,
                    )
                  }
                  userRole={props.userRole}
                />
              );
            });
          }
          case 'deposit': {
            const deposit = props.documents[key] as DepositDoc;
            return (
              <Deposit
                key={'despoit-doc'}
                id={'deposit'}
                infoOnly={infoOnly}
                userAccess={userAccess}
                depositAmount={props.financials.courseFeeDeposit}
                document={deposit}
                canResume={canResume}
                canUpload={canUpload}
                canOpen={canOpen}
                canApprove={canApprove}
                canReject={canReject}
                isDeleting={isDeleting}
                canAcknowledge={canAcknowledge}
                onResume={onResume}
                onUploadClick={(docKey: string) => {
                  props.openUploadFinanceModal(null, null, 'deposit', docKey, 'Deposit', deposit[docKey].fileNames);
                }}
                onOpenClick={(docKey: string) => {
                  setImageArray(deposit[docKey].signedUrls);
                  toggleOpen(true);
                }}
                onApproveClick={(docKey: string) =>
                  props.onApproveDoc(null, null, 'deposit', docKey, deposit[docKey].status, deposit[docKey].prevStatus)
                }
                onRejectClick={(docKey: string) =>
                  props.openFinanceDocumentModal(null, null, 'reject-deposit', docKey, 'Deposit')
                }
                onRescindRejectClick={(docKey: string) =>
                  props.openFinanceDocumentModal(null, null, 'rescind-reject-deposit', docKey, 'Deposit')
                }
                onDeleteClick={(docKey: string) =>
                  props.openFinanceDocumentModal(null, null, 'delete-deposit', docKey, 'Deposit')
                }
                onAcknowledgeClick={(docKey: string) =>
                  props.onAcknowledgeDoc(
                    null,
                    null,
                    'deposit',
                    docKey,
                    deposit[docKey].status,
                    deposit[docKey].prevStatus,
                  )
                }
                userRole={props.userRole}
              />
            );
          }
          case 'scholarshipSponsorship': {
            const scholarshipSponsorMap = props.documents[key] as ScholarshipDocsMap;

            return Object.entries(scholarshipSponsorMap).map(([scholarKey, scholarSponsor]) => {
              const scholarshipSponsorFinance = props.financials.scholarshipSponsorship?.[scholarKey];

              if (!scholarshipSponsorFinance) {
                return null;
              }
              return (
                <ScholarshipSponsor
                  key={scholarKey}
                  id={scholarKey}
                  infoOnly={infoOnly}
                  userAccess={userAccess}
                  documents={scholarSponsor}
                  financials={scholarshipSponsorFinance}
                  risk={props.risks.fundingRisk.scholarshipSponsorship}
                  canResume={canResume}
                  canUpload={canUpload}
                  canOpen={canOpen}
                  canApprove={canApprove}
                  canReject={canReject}
                  isDeleting={isDeleting}
                  canAcknowledge={canAcknowledge}
                  showRisks={showRisks}
                  canCheckOff={canCheckOffRisks}
                  isCheckedOffDocRisks={(docType: string, docKey: string) => {
                    return checkCheckedOffDocRisks(docType, docKey);
                  }}
                  onCheckOffDocRisks={(docType: string, docKey: string, docTitle: string) => {
                    handleUpdateDocRisksClick(docType, docKey, docTitle);
                  }}
                  onResume={onResume}
                  onUploadClick={(docKey: string) => {
                    props.openUploadFinanceModal(
                      'scholarshipSponsorship',
                      scholarKey,
                      null,
                      docKey,
                      `${scholarshipSponsorFinance.scholarshipName}`,
                      scholarSponsor[docKey].fileNames,
                    );
                  }}
                  onOpenClick={(docKey: string) => {
                    setImageArray(scholarSponsor[docKey].signedUrls);
                    toggleOpen(true);
                  }}
                  onApproveClick={(docKey: string) =>
                    props.onApproveDoc(
                      'scholarshipSponsorship',
                      scholarKey,
                      null,
                      docKey,
                      scholarSponsor[docKey].status,
                      scholarSponsor[docKey].prevStatus,
                    )
                  }
                  onRejectClick={(docKey: string) =>
                    props.openFinanceDocumentModal(
                      'scholarshipSponsorship',
                      scholarKey,
                      'reject-null',
                      docKey,
                      'Scholarship Letter Scan',
                    )
                  }
                  onRescindRejectClick={(docKey: string) =>
                    props.openFinanceDocumentModal(
                      'scholarshipSponsorship',
                      scholarKey,
                      'rescind-reject-null',
                      docKey,
                      'Scholarship Letter Scan',
                    )
                  }
                  onDeleteClick={(docKey: string) =>
                    props.openFinanceDocumentModal(
                      'scholarshipSponsorship',
                      scholarKey,
                      'delete-null',
                      docKey,
                      'Scholarship Letter Scan',
                    )
                  }
                  onAcknowledgeClick={(docKey: string) =>
                    props.onAcknowledgeDoc(
                      'scholarshipSponsorship',
                      scholarKey,
                      null,
                      docKey,
                      scholarSponsor[docKey].status,
                      scholarSponsor[docKey].prevStatus,
                    )
                  }
                  userRole={props.userRole}
                />
              );
            });
          }
          case 'customRequest': {
            const customRequestDocs = props.documents[key] as CustomRequestMap;
            return (
              <CustomRequestDocument
                key={key}
                infoOnly={infoOnly}
                userAccess={userAccess}
                documents={customRequestDocs}
                canUpload={canUpload}
                canOpen={canOpen}
                canApprove={canApprove}
                canReject={canReject}
                isDeleting={isDeleting}
                canAcknowledge={canAcknowledge}
                onOpenClick={(customReqKey: string, docKey: string) => {
                  setImageArray(customRequestDocs[customReqKey][docKey].signedUrls!);
                  toggleOpen(true);
                }}
                onUploadClick={(customReqKey: string, docKey: string) => {
                  props.openUploadFinanceModal(
                    'customRequest',
                    customReqKey,
                    null,
                    docKey,
                    'Custom Request Document',
                    customRequestDocs[customReqKey][docKey].fileNames,
                  );
                }}
                onApproveClick={(customReqKey: string, docKey: string) =>
                  props.onApproveDoc(
                    'customRequest',
                    customReqKey,
                    null,
                    docKey,
                    customRequestDocs[customReqKey][docKey].status,
                    customRequestDocs[customReqKey][docKey].prevStatus,
                  )
                }
                onRejectClick={(customReqKey: string, docKey: string) =>
                  props.openFinanceDocumentModal(
                    'customRequest',
                    customReqKey,
                    'reject-null',
                    docKey,
                    'Custom Document',
                  )
                }
                onRescindRejectClick={(customReqKey: string, docKey: string) =>
                  props.openFinanceDocumentModal(
                    'customRequest',
                    customReqKey,
                    'rescind-reject-null',
                    docKey,
                    'Custom Document',
                  )
                }
                onDeleteClick={(customReqKey: string, docKey: string) =>
                  props.openFinanceDocumentModal(
                    'customRequest',
                    customReqKey,
                    'delete-null',
                    docKey,
                    'Custom Document',
                  )
                }
                onAcknowledgeClick={(customReqKey: string, docKey: string) =>
                  props.onAcknowledgeDoc(
                    'customRequest',
                    customReqKey,
                    null,
                    docKey,
                    customRequestDocs[customReqKey][docKey].status,
                    customRequestDocs[customReqKey][docKey].prevStatus,
                  )
                }
                userRole={props.userRole}
              />
            );
          }
          case 'governmentSponsorship':
          case 'companySponsorship': {
            const organisationSponsorMap = props.documents[key] as OrganisationDocsMap;
            return Object.entries(organisationSponsorMap).map(([orgKey, orgSponsor]) => {
              const orgSponsorFinance = props.financials.organisationSponsorship?.[orgKey];
              if (!orgSponsorFinance) {
                return null;
              }
              return (
                <GovernmentOrCompany
                  key={orgKey}
                  id={orgKey}
                  infoOnly={infoOnly}
                  userAccess={userAccess}
                  sponsorType={key}
                  documents={orgSponsor}
                  financials={orgSponsorFinance}
                  risk={props.risks.fundingRisk.organisationSponsorship}
                  docRisk={props.risks.financeDocsRisk.organisationSponsorship}
                  canResume={canResume}
                  canUpload={canUpload}
                  canOpen={canOpen}
                  canApprove={canApprove}
                  canReject={canReject}
                  isDeleting={isDeleting}
                  canAcknowledge={canAcknowledge}
                  onResume={onResume}
                  showRisks={showRisks}
                  canCheckOff={canCheckOffRisks}
                  isCheckedOffDocRisks={(docType: string, docKey: string) => {
                    return checkCheckedOffDocRisks(docType, docKey);
                  }}
                  onCheckOffDocRisks={(docType: string, docKey: string, docTitle: string) => {
                    handleUpdateDocRisksClick(docType, docKey, docTitle);
                  }}
                  isCheckedOffFinanceRisks={(docType: string, docKey: string) => {
                    return checkCheckedOffFinanceRisks(docType, docKey);
                  }}
                  onCheckOffFinanceRisks={(docType: string, docKey: string, docTitle: string) => {
                    handleUpdateFinanceRisksClick(docType, docKey, docTitle);
                  }}
                  onUploadClick={(docKey: string) => {
                    props.openUploadFinanceModal(
                      key,
                      orgKey,
                      null,
                      docKey,
                      'Organisation Sponsor',
                      props.documents[key][orgKey][docKey].fileNames,
                    );
                  }}
                  onOpenClick={(docKey: string) => {
                    setImageArray(props.documents[key][orgKey][docKey].signedUrls);
                    toggleOpen(true);
                  }}
                  onApproveClick={(docKey: string) =>
                    props.onApproveDoc(
                      key,
                      orgKey,
                      null,
                      docKey,
                      props.documents[key][orgKey][docKey].status,
                      props.documents[key][orgKey][docKey].prevStatus,
                    )
                  }
                  onRejectClick={(docKey: string) =>
                    props.openFinanceDocumentModal(key, orgKey, 'reject-null', docKey, 'Organisation Document')
                  }
                  onRescindRejectClick={(docKey: string) =>
                    props.openFinanceDocumentModal(key, orgKey, 'rescind-reject-null', docKey, 'Organisation Document')
                  }
                  onDeleteClick={(docKey: string) =>
                    props.openFinanceDocumentModal(key, orgKey, 'delete-null', docKey, 'Organisation Document')
                  }
                  onAcknowledgeClick={(docKey: string) =>
                    props.onAcknowledgeDoc(
                      key,
                      orgKey,
                      null,
                      docKey,
                      props.documents[key][orgKey][docKey].status,
                      props.documents[key][orgKey][docKey].prevStatus,
                    )
                  }
                  userRole={props.userRole}
                />
              );
            });
          }
          case 'evidenceOfIncome': {
            const evidenceOfIncomeDocs = props.documents[key] as EvidenceOfIncomeDocs;
            if (
              Object.keys(evidenceOfIncomeDocs?.curriculumVitae ?? {}).length === 0 &&
              Object.keys(evidenceOfIncomeDocs?.employmentReference ?? {}).length === 0 &&
              Object.keys(evidenceOfIncomeDocs?.recentEmploymentContract ?? {}).length === 0 &&
              Object.keys(evidenceOfIncomeDocs?.recentPayslip ?? {}).length === 0
            ) {
              return null;
            }
            const leadSponsor = getLeadSponsorByKey(props.financials);
            return (
              <EvidenceOfIncomeSection
                key={`employment-docs-${evidenceOfIncomeDocs.leadSponsor!}`}
                id={'evidence_of_income'}
                infoOnly={infoOnly}
                userAccess={userAccess}
                leadSponsor={leadSponsor}
                documents={evidenceOfIncomeDocs}
                docRisk={props.risks.financeDocsRisk.evidenceOfIncome}
                canResume={canResume}
                canUpload={canUpload}
                canOpen={canOpen}
                canApprove={canApprove}
                canReject={canReject}
                isDeleting={isDeleting}
                canAcknowledge={canAcknowledge}
                showRisks={showRisks}
                canCheckOff={canCheckOffRisks}
                isCheckedOffDocRisks={(docType: string, docKey: string) => {
                  return checkCheckedOffDocRisks(docType, docKey);
                }}
                onCheckOffDocRisks={(docType: string, docKey: string, docTitle: string) => {
                  handleUpdateDocRisksClick(docType, docKey, docTitle);
                }}
                isCheckedOffFinanceRisks={(docType: string, docKey: string) => {
                  return checkCheckedOffDocRisks(docType, docKey);
                }}
                onCheckOffFinanceRisks={(docType: string, docKey: string, docTitle: string) => {
                  handleUpdateDocRisksClick(docType, docKey, docTitle);
                }}
                onResume={onResume}
                onUploadClick={(sponsorType: string, docType: string | null, docKey: string, modalTitle: string) => {
                  props.openUploadFinanceModal(
                    sponsorType,
                    null,
                    docType,
                    docKey,
                    modalTitle,
                    props.documents.evidenceOfIncome[docType!][docKey].fileNames,
                  );
                }}
                onOpenClick={(_sponsorType: string, docType: string | null, docKey: string) => {
                  setImageArray(props.documents.evidenceOfIncome[docType!][docKey].signedUrls);
                  toggleOpen(true);
                }}
                onApproveClick={(sponsorType: string, docType: string | null, docKey: string) =>
                  props.onApproveDoc(
                    sponsorType,
                    null,
                    docType,
                    docKey,
                    props.documents.evidenceOfIncome[docType!][docKey].status,
                    props.documents.evidenceOfIncome[docType!][docKey].prevStatus,
                  )
                }
                onRejectClick={(sponsorType: string, docType: string | null, docKey: string, modalTitle: string) =>
                  props.openFinanceDocumentModal(sponsorType, null, `reject-${docType}`, docKey, modalTitle)
                }
                onRescindRejectClick={(
                  sponsorType: string,
                  docType: string | null,
                  docKey: string,
                  modalTitle: string,
                ) => props.openFinanceDocumentModal(sponsorType, null, `rescind-reject-${docType}`, docKey, modalTitle)}
                onDeleteClick={(sponsorType: string, docType: string | null, docKey: string, modalTitle: string) =>
                  props.openFinanceDocumentModal(sponsorType, null, `delete-${docType}`, docKey, modalTitle)
                }
                onAcknowledgeClick={(sponsorType: string, docType: string | null, docKey: string) =>
                  props.onAcknowledgeDoc(
                    sponsorType,
                    null,
                    docType,
                    docKey,
                    props.documents.evidenceOfIncome[docType!][docKey].status,
                    props.documents.evidenceOfIncome[docType!][docKey].prevStatus,
                  )
                }
                userRole={props.userRole}
              />
            );
          }
          default:
            return null;
        }
      });
  };

  const { openModal } = props;
  if (openModal) {
    if (openModal.isUpload) {
      return <FileUploadModal {...openModal} />;
    }

    if (openModal.sponsorType === 'customRequestDocument') {
      return <CustomRequestDocumentModal />;
    }
    if (openModal.docType?.startsWith('delete-')) {
      return <FileDeleteModal onDelete={() => setIsDeleting(false)} />;
    }
    if (openModal.docType?.startsWith('reject-')) {
      return <FileRejectModal />;
    }
    if (openModal.docType?.startsWith('rescind-reject-')) {
      return <FileRescindRejectionModal />;
    }
  }

  const totalAmountToProve =
    (props?.financials?.fundingSummary?.totalInvalid || props?.financials?.fundingSummary?.totalRemaining) ?? 0;

  const getTotalAmountToProveLabel = (totalAmountToProve?: number): string | React.ReactNode => {
    let totalAmountToProveLabel: string | React.ReactNode = `£${formatCurrency(`${totalAmountToProve}`)}`;
    if (Number(totalAmountToProve) <= 0) {
      totalAmountToProveLabel = 'None';
    }
    return totalAmountToProveLabel;
  };

  return (
    <>
      {props.courses?.length ? (
        <Flex
          data-selector="total-section"
          alignItems={{ xl: 'center' }}
          bgColor={'chakra-subtle-bg'}
          flexDirection={{ base: 'column', xl: 'row' }}
          pl={{ base: 4, md: 6, xl: 6 }}
          pr={{ base: 3, md: 6, xl: 4 }}
          py={4}
          mb={6}
          borderRadius={'xl'}
        >
          <Box flexGrow={1} mb={{ base: 3, xl: 0 }}>
            <Box fontWeight={'medium'} mb={2}>
              Amount missing from Finance Plan
            </Box>

            <Badge
              data-selector="fin-docs-amount-to-prove"
              size={'md'}
              colorScheme={totalAmountToProve > 0 ? 'orange' : 'green'}
            >
              {getTotalAmountToProveLabel(totalAmountToProve)}
            </Badge>
          </Box>

          {canResume && !agentUploadLock && (
            <Button onClick={() => onResume('finances/step-1')}>Add To/Change Finance Plan</Button>
          )}
        </Flex>
      ) : null}

      {getInvalidFunding()}

      {props.hasRequiredDocs || props.hasAgentOnlyDocs || props.hasInfoOnlyDocs || props.hasStaffOnlyDocs ? (
        <>
          {isSectionLocked && <LockedSectionWarning />}

          {renderDocs(false)}

          {props.hasStaffOnlyDocs && canUploadStaffOnly ? (
            <SectionGroup
              heading="The below documents can only be uploaded by a staff member"
              description="The student has not been asked to provide these documents"
              data-selector={'staff-only-docs-section'}
            >
              {renderDocs(false, DocAccessType.STAFF_ONLY)}
            </SectionGroup>
          ) : null}

          {props.hasAgentOnlyDocs && canUploadAgentOnly ? (
            <SectionGroup
              heading="The below documents can only be uploaded by an agent"
              description="The student has not been asked to provide these documents"
              data-selector={'agent-only-docs-section'}
            >
              {renderDocs(false, DocAccessType.AGENT_ONLY)}
            </SectionGroup>
          ) : null}

          {props.hasInfoOnlyDocs ? (
            <SectionGroup
              heading="The below documents may be spot checked during the visa application process"
              description="No upload is required, the student has been asked to read and confirm they understand. If you have
                  notified the student personally, click the Acknowledge button."
              data-selector={'info-only-docs-section'}
            >
              {renderDocs(true)}
            </SectionGroup>
          ) : null}
        </>
      ) : (
        <>
          {(props.hasStaffOnlyDocs && canUploadStaffOnly) || (props.hasAgentOnlyDocs && canUploadAgentOnly) ? (
            <>
              {props.hasStaffOnlyDocs && canUploadStaffOnly ? (
                <SectionGroup
                  heading="The below documents can only be uploaded by a staff member"
                  description="The student has not been asked to provide these documents"
                  data-selector={'staff-only-docs-section'}
                >
                  {renderDocs(false, DocAccessType.STAFF_ONLY)}
                </SectionGroup>
              ) : null}

              {props.hasAgentOnlyDocs && canUploadAgentOnly ? (
                <SectionGroup
                  heading="The below documents can only be uploaded by an agent"
                  description="The student has not been asked to provide these documents"
                  data-selector={'agent-only-docs-section'}
                >
                  {renderDocs(false, DocAccessType.AGENT_ONLY)}
                </SectionGroup>
              ) : null}
            </>
          ) : (
            <>
              {isCasBypass && <CASBypassWarning />}

              <NoDocuments />
            </>
          )}
        </>
      )}

      <DocumentRequestButtons
        canRequest={canRequest}
        canDelete={canDelete}
        isDeleting={isDeleting}
        onRequestClick={() => props.openFinanceDocumentModal('customRequestDocument', null, '', null)}
        onDeleteClick={() => setIsDeleting(!isDeleting)}
      />

      {previewOpen && imageArray && imageArray.length > 0 && (
        <DocumentViewer documents={imageArray} onClose={() => toggleOpen(false)} />
      )}
    </>
  );
}

export default DocumentDashboard;
